import { useEffect, useMemo, useState } from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandShortcut } from "@/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/ui/dialog";
import { Kbd } from "@/ui/kbd";
import * as api from "../api.js";
import { procurar } from "../ajuda/busca.js";
import { ATALHO_DA_PALETA, GRUPOS_DO_RAIL, ICONES, Icone, RAIL, TITULOS, railDe } from "../visual.jsx";
import { Avatar } from "../componentes/comuns.jsx";
import { useAcoes, useEstado, usePode } from "../estado.jsx";
import { comecarDemonstracao } from "../demonstracao/Demonstracao.jsx";
import { CAMINHO_POR_VISTA, useNavegacao } from "../navegacao.jsx";
import { cru, verbosDaPaleta } from "../paleta.js";
import { anotarPaletaUsada } from "../primeiros-passos.js";
import { confirmar } from "@/componentes/pergunta.tsx";

/**
 * A paleta: um lugar para digitar para onde se quer ir.
 *
 * É a navegação que ganha da navegação. O trilho é ótimo para as onze vistas e
 * não serve para o que a mesa tem de verdade — quinhentos e vinte e oito
 * atendimentos: achar "a Angélica" pelo trilho é ir em Atendimentos, esperar a
 * lista, digitar na busca e clicar. Aqui são três teclas e o nome.
 *
 * A busca de atendimento é NO SERVIDOR (`mesaPagina`), e não na `mesa` que a
 * tela já tem: aquela é uma PÁGINA de 200 de 528, e paleta que só acha quem já
 * está carregado é pior que não achar — responde "nada com esse nome" sobre um
 * cliente que existe. Mesmo motivo do contador da pastilha.
 *
 * Os recentes são a outra metade: o dia do escritório é ir e voltar entre dois
 * ou três lugares, e com eles a paleta aberta já mostra o destino antes de
 * qualquer tecla. Ficam no `localStorage` (é o rastro DESTA máquina, e não dado
 * do escritório) e nunca incluem onde a pessoa está agora.
 *
 * Ela acha CINCO coisas, e não duas: vista, atendimento, tarefa, resposta pronta
 * (pelo atalho — é como se pensa nela: `/docs`) e pessoa da equipe. E FAZ cinco
 * (ver `VERBOS`), que é o que faltava: paleta que só leva a lugar deixa quem
 * quer criar algo procurando o botão na tela certa. Três dessas
 * saem de dado que a mesa já carregou, sem um pedido a mais; só a tarefa é ida
 * ao servidor, pelo mesmo `q` que a tela de Tarefas usa.
 *
 * Arquivo do acervo ficou FORA: `GET /api/documentos/acervo` monta o resumo de
 * todas as pastas em toda chamada, e ler o disco do escritório a cada duas
 * teclas é caro para o quinto grupo de uma lista. Quando houver busca de arquivo
 * que não varre pasta, ele entra aqui.
 *
 * Cada grupo respeita a permissão de quem digita — a mesma regra do trilho. A
 * paleta mostrava as onze vistas para todo mundo, e "ir" para a que responde 403
 * é um beco com atalho de teclado.
 */

const CHAVE_RECENTES = "galera.paleta.recentes";
const TETO_DE_RECENTES = 6;

const lerRecentes = () => {
  try {
    const bruto = JSON.parse(localStorage.getItem(CHAVE_RECENTES) ?? "[]");
    return Array.isArray(bruto) ? bruto.filter((r) => r?.caminho && r?.rotulo).slice(0, TETO_DE_RECENTES) : [];
  } catch {
    return [];
  }
};

/*
 * Guarda o lugar visitado no topo da pilha, sem repetir.
 *
 * A identidade é a VISTA, não o caminho: `/` e `/atendimentos` são o mesmo
 * lugar (a raiz redireciona), e sem isso a lista de recentes mostrava
 * "Atendimentos" duas vezes, uma delas levando ao redirecionamento. Atendimento
 * de cliente não tem vista própria, então ali a identidade é o caminho mesmo.
 */
const identidade = (lugar) => lugar.chave ?? lugar.caminho;

export const anotarRecente = (lugar) => {
  try {
    const antes = lerRecentes().filter((r) => identidade(r) !== identidade(lugar));
    localStorage.setItem(CHAVE_RECENTES, JSON.stringify([lugar, ...antes].slice(0, TETO_DE_RECENTES)));
  } catch {
    /* aba em modo restrito: a paleta segue funcionando sem memória */
  }
};

/* De que grupo do trilho cada vista é — é o que a paleta mostra à direita do
   nome, para "Ajustes" e "Fluxos" não parecerem a mesma coisa na lista. */
const GRUPO_DA_VISTA = Object.fromEntries(GRUPOS_DO_RAIL.flatMap(({ titulo, itens }) => itens.map((i) => [i.chave, titulo])));

export default function Paleta({ aoFechar }) {
  const nav = useNavegacao();
  const pode = usePode();
  /* Rápidas e equipe chegam com a mesa (`rotas/mesa.ts`), então procurar nelas
     não custa pedido nenhum. */
  const { rapidas, equipe, workspace, workspaces } = useEstado();
  const { novoCaso, puxarFila, trocarEscritorio, avisar } = useAcoes();
  /* Abrir a paleta é o dado que cumpre o passo "conhecer a paleta" de quem
     atende — ver `primeiros-passos.js`. */
  useEffect(() => {
    anotarPaletaUsada();
  }, []);
  const [termo, setTermo] = useState("");
  const [casos, setCasos] = useState([]);
  const [tarefas, setTarefas] = useState([]);
  const [buscando, setBuscando] = useState(false);
  /* Lido UMA vez, na abertura: a paleta é montada a cada Ctrl+K (é o que faz
     ela nascer limpa, sem o termo da pergunta anterior), então este `useState`
     já é "os recentes de agora". */
  const [recentes] = useState(lerRecentes);

  /*
   * O atendimento vem do servidor, adiado e cancelável.
   *
   * 180ms porque a paleta é digitada rápido e cada tecla seria uma consulta na
   * mesa inteira; `AbortController` porque a resposta da terceira letra pode
   * chegar depois da resposta da segunda — sem cortar, a lista pisca para trás.
   * Uma letra não busca: "a" traz meia mesa e nenhuma resposta útil.
   */
  useEffect(() => {
    const busca = termo.trim();
    if (busca.length < 2) return undefined;
    const controle = new AbortController();
    const relogio = setTimeout(async () => {
      setBuscando(true);
      /* Os dois pedidos juntos, e cada um caindo sozinho: quem não alcança
         Tarefas continua achando cliente, e o inverso também. `Promise.all`
         derrubaria os dois por causa de um 403 num deles. */
      const [mesa, quadro] = await Promise.all([
        pode("leads:ver")
          ? api.mesaPagina({ q: busca, limite: 6, sinal: controle.signal }).catch(() => null)
          : Promise.resolve(null),
        pode("registros:ver") ? api.tarefas({ q: busca, so_abertas: 1 }).catch(() => null) : Promise.resolve(null),
      ]);
      /* Rede caída não vira balão vermelho aqui: a paleta continua achando
         vista, e o erro da mesa aparece onde a mesa está. */
      setCasos(mesa?.corpo?.atendimentos ?? []);
      /* Seis, como os atendimentos: a paleta é para ir a UMA coisa que se sabe o
         nome, e não para varrer o quadro — quem quer varrer vai em Tarefas. */
      setTarefas((quadro?.tarefas ?? []).slice(0, 6));
      setBuscando(false);
    }, 180);
    return () => {
      clearTimeout(relogio);
      controle.abort();
    };
  }, [pode, termo]);

  /* Apagar letras não pode deixar o resultado antigo na tela: o que vale é
     DERIVADO do termo de agora, não limpado por efeito depois. */
  const digitou = termo.trim().length >= 2;
  const achados = digitou ? casos : [];
  const prazos = digitou ? tarefas : [];

  const irPara = (caminho) => {
    aoFechar();
    nav.ir(caminho);
  };

  const vistas = useMemo(() => {
    const busca = cru(termo.trim());
    /* Digitou: casa pelo nome do lugar E pelo nome do grupo, que é como se
       pensa nele ("acervo", "escritório"). Vazio: as que a pessoa alcança, na
       ordem do trilho. */
    return railDe(pode).itens.filter(
      (i) => !busca || cru(i.rotulo).includes(busca) || cru(GRUPO_DA_VISTA[i.chave]).includes(busca),
    );
  }, [pode, termo]);

  /*
   * Resposta pronta pelo ATALHO, e depois pelo texto.
   *
   * O atalho é como o escritório chama a resposta (`/docs`, `/hon`), e a barra
   * na frente não pode atrapalhar: quem digita "docs" e quem digita "/docs" quer
   * a mesma coisa. Daqui a paleta leva para `/rapidas?q=`, que é onde ela se lê
   * e se edita — inserir na conversa é gesto da conversa, com um caso aberto.
   */
  const prontas = useMemo(() => {
    const busca = cru(termo.trim()).replace(/^\/+/, "");
    if (busca.length < 2) return [];
    return (rapidas ?? []).filter((r) => cru(r.atalho).includes(busca) || cru(r.texto).includes(busca)).slice(0, 5);
  }, [rapidas, termo]);

  /*
   * O manual entra na paleta.
   *
   * Quem não sabe fazer alguma coisa digita o NOME DA COISA ("conferir",
   * "protocolo", "cobrança"), e não "ajuda". Se a paleta só oferecesse áreas e
   * clientes, o manual continuaria sendo aquele lugar que ninguém abre. São três
   * no máximo: a paleta é para IR, não para ler.
   */
  const doManual = useMemo(() => (termo.trim().length < 2 ? [] : procurar(termo, 3)), [termo]);

  /*
   * Os verbos que esta pessoa alcança, casando por rótulo E por sinônimo.
   *
   * Sem termo eles ficam todos: a paleta aberta é onde alguém descobre que dá
   * para criar coisa daqui, e esconder até a primeira tecla é esconder de quem
   * não sabe o que digitar.
   */
  /* Os recentes que ainda fazem sentido mostrar: nunca o lugar de agora — "ir
     para onde você já está" é a linha que gasta a primeira posição da lista. */
  const doHistorico = useMemo(() => recentes.filter((r) => r.caminho !== nav.caminho), [recentes, nav.caminho]);

  const verbos = useMemo(
    () => verbosDaPaleta({ pode, casoId: nav.casoId, termo, workspace, workspaces }),
    [nav.casoId, pode, termo, workspace, workspaces],
  );

  /* O que cada verbo faz. Fora do `useMemo` porque é ação, e ação não entra em
     lista de dependência — o catálogo é dado, isto é o motor dele. */
  const fazer = (verbo) => {
    aoFechar();
    if (verbo.caminho) return nav.ir(verbo.caminho);
    if (verbo.chave === "novo-atendimento") return novoCaso();
    if (verbo.chave === "puxar-fila") return puxarFila();
    if (verbo.chave === "demonstracao") return comecarDemonstracao();
    if (verbo.chave === "etiquetar") return nav.ajustar({ etiquetas: nav.casoId });
    if (verbo.setor) return trocarEscritorio(verbo.setor);
    if (verbo.modo) return mudarModo(verbo);
    return undefined;
  };

  /* O modo do setor pela paleta: a mesma pergunta do cartão de Ajustes para o
     piloto (o único em que sai mensagem sem aprovação) e o mesmo recibo. */
  const mudarModo = async (verbo) => {
    if (verbo.modo === "piloto") {
      const ok = await confirmar({
        titulo: "Ligar o piloto automático?",
        texto:
          "Os agentes passam a falar com o cliente sem esperar aprovação: resposta, cobrança e contrato saem sozinhos. Protocolar continua de gente.",
        acao: "Ligar o piloto",
        perigo: true,
      });
      if (!ok) return;
    }
    try {
      await api.definirModoDaIA(verbo.modo);
      avisar(`${verbo.rotulo}. Vale para o setor inteiro.`);
    } catch (e) {
      avisar(e.message);
    }
  };

  /* Pessoa da equipe → a ficha dela. `equipe` vem com a mesa, e o nome é o que
     se digita. */
  const pessoas = useMemo(() => {
    const busca = cru(termo.trim());
    if (busca.length < 2 || !pode("equipe:ver")) return [];
    return (equipe ?? []).filter((p) => cru(p.nome).includes(busca)).slice(0, 5);
  }, [equipe, pode, termo]);

  return (
    <Dialog open onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent
        showCloseButton={false}
        className="top-[14vh] translate-y-0 overflow-hidden rounded-2xl! p-0 sm:max-w-[520px]"
      >
        <DialogTitle className="sr-only">Ir para</DialogTitle>
        <DialogDescription className="sr-only">
          Digite o nome de uma área da mesa ou de um cliente para ir até lá, ou o que você quer fazer.
        </DialogDescription>
        {/*
         * `shouldFilter={false}`: quem filtra é o servidor (atendimento) e o
         * `vistas` daqui. O filtro do `cmdk` casa só com o texto desenhado, então
         * ele escondia o cliente achado por TELEFONE ou por etapa — resultado
         * certo, sumido pela segunda peneira.
         */}
        <Command shouldFilter={false} loop className="rounded-2xl">
          <CommandInput value={termo} onValueChange={setTermo} placeholder="Ir para, ou fazer: cliente, prazo, área…" autoFocus />
          <CommandList className="max-h-[min(62vh,440px)]">
            <CommandEmpty className="px-3 py-6 text-center text-corpo text-muted-foreground">
              {buscando ? "Procurando…" : "Nada com esse nome — nem área, nem cliente, nem prazo, nem ação."}
            </CommandEmpty>

            {/* Conta o que VAI DESENHAR, e não o que está guardado: o guarda era
                `recentes.length` e a lista tirava o lugar de agora logo abaixo —
                com um recente só, e sendo ele onde a pessoa está, a paleta abria
                com um título de grupo e nada embaixo dele. */}
            {!termo.trim() && doHistorico.length > 0 && (
              <CommandGroup heading="Onde você estava">
                {doHistorico.map((r) => (
                  <CommandItem key={r.caminho} value={`recente ${r.caminho}`} onSelect={() => irPara(r.caminho)}>
                    {/* O ícone é RESOLVIDO do `RAIL`, nunca guardado: componente
                          não sobrevive ao `JSON.stringify`, e renomear um ícone
                          deixaria o recente desenhando o de ontem. */}
                    <Icone de={RAIL.find((i) => i.chave === r.chave)?.icone ?? ICONES.chats} tamanho={16} />
                    <span className="min-w-0 truncate">{r.rotulo}</span>
                    <CommandShortcut className="tracking-normal">
                      {r.chave ? GRUPO_DA_VISTA[r.chave] : "atendimento"}
                    </CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Lugar FIXO na lista, logo depois dos recentes: posição que muda
                com o que se digitou é posição que ninguém aprende. */}
            {verbos.length > 0 && (
              <CommandGroup heading="Fazer">
                {verbos.map((verbo) => (
                  <CommandItem key={verbo.chave} value={`fazer ${verbo.chave}`} onSelect={() => fazer(verbo)}>
                    <Icone de={verbo.icone} tamanho={16} />
                    <span className="min-w-0 truncate">{verbo.rotulo}</span>
                    {verbo.nota && <CommandShortcut className="truncate tracking-normal">{verbo.nota}</CommandShortcut>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {vistas.length > 0 && (
              <CommandGroup heading="Áreas da mesa">
                {vistas.map((item) => (
                  <CommandItem
                    key={item.chave}
                    value={`vista ${item.chave}`}
                    onSelect={() => {
                      aoFechar();
                      nav.irParaVista(item.chave);
                    }}
                  >
                    <Icone de={item.icone} tamanho={16} />
                    <span className="min-w-0 truncate">{item.rotulo}</span>
                    <CommandShortcut className="tracking-normal">{GRUPO_DA_VISTA[item.chave]}</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {achados.length > 0 && (
              <CommandGroup heading="Atendimentos">
                {achados.map((caso) => (
                  <CommandItem
                    key={caso.id}
                    value={`caso ${caso.id}`}
                    onSelect={() => irPara(`/atendimentos/${caso.id}`)}
                    className="gap-2.5"
                  >
                    <Avatar nome={caso.nome} foto={caso.foto} tamanho={22} raio={7} fonte={9} />
                    <span className="min-w-0 truncate">{caso.nome}</span>
                    {caso.etapa && <CommandShortcut className="truncate tracking-normal">{caso.etapa}</CommandShortcut>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {prazos.length > 0 && (
              <CommandGroup heading="Tarefas e prazos">
                {prazos.map((t) => (
                  /* Aponta A TAREFA (`?tarefa=`), não a lista: a paleta prometia
                     levar a um item e caía no quadro inteiro, com a pessoa
                     procurando o que ela já tinha achado aqui. */
                  <CommandItem key={t.id} value={`tarefa ${t.id}`} onSelect={() => irPara(`/tarefas?tarefa=${t.id}`)}>
                    <Icone de={ICONES.tarefas} tamanho={16} />
                    <span className="min-w-0 truncate">{t.titulo}</span>
                    <CommandShortcut className="truncate tracking-normal">
                      {t.nome ?? t.responsavel ?? "sem responsável"}
                    </CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {prontas.length > 0 && (
              <CommandGroup heading="Respostas rápidas">
                {prontas.map((r) => (
                  <CommandItem
                    key={r.id ?? r.atalho}
                    value={`rapida ${r.atalho}`}
                    onSelect={() => irPara(`/rapidas?q=${encodeURIComponent(r.atalho.replace(/^\/+/, ""))}`)}
                  >
                    <Icone de={ICONES.rapidas} tamanho={16} />
                    <span className="min-w-0 truncate font-mono text-corpo">{r.atalho}</span>
                    <CommandShortcut className="truncate tracking-normal">{r.categoria || "resposta pronta"}</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {doManual.length > 0 && (
              <CommandGroup heading="Na ajuda">
                {doManual.map((r) => (
                  <CommandItem
                    key={`${r.topico}-${r.secao}`}
                    value={`ajuda ${r.topico} ${r.secao}`}
                    onSelect={() => irPara(`/ajuda/${r.topico}`)}
                  >
                    <Icone de={ICONES.ajuda} tamanho={16} />
                    <span className="min-w-0 truncate">{r.titulo}</span>
                    <CommandShortcut className="truncate tracking-normal">{r.tituloDoTopico}</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {pessoas.length > 0 && (
              <CommandGroup heading="Equipe">
                {pessoas.map((p) => (
                  <CommandItem
                    key={p.id}
                    value={`pessoa ${p.id}`}
                    onSelect={() => irPara(`/equipe?pessoa=${p.id}`)}
                    className="gap-2.5"
                  >
                    {/* Sem `foto`: `people` guarda `cor`, não avatar — o monograma
                        é o retrato da pessoa aqui, o mesmo da lista da mesa. */}
                    <Avatar nome={p.nome} tamanho={22} raio={7} fonte={9} />
                    <span className="min-w-0 truncate">{p.nome}</span>
                    <CommandShortcut className="truncate tracking-normal">
                      {p.tarefas_abertas ? `${p.tarefas_abertas} tarefa(s)` : p.funcao || "equipe"}
                    </CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>

          {/* O pé ensina o atalho: paleta que só se abre pelo mouse é campo de
              busca com passos extras. No dedo não há teclado para ensinar, e
              as duas frases quebravam em quatro linhas no pé da caixa. */}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-border px-3 py-2 text-selo text-muted-foreground pointer-coarse:hidden">
            <span>
              <Kbd>↑</Kbd> <Kbd>↓</Kbd> para escolher · <Kbd>Enter</Kbd> para ir ou fazer
            </span>
            <span>
              <Kbd>{ATALHO_DA_PALETA}</Kbd> abre de qualquer lugar
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

/**
 * O que a paleta guarda de um lugar visitado. Só o que sobrevive ao
 * `localStorage`: caminho, nome e a chave da vista.
 */
export function lugarParaRecente({ vista, casoId, nomeDoCaso, caminho }) {
  if (casoId) return { caminho, rotulo: nomeDoCaso ?? `Atendimento #${casoId}` };
  /* O caminho CANÔNICO da vista, e não o da barra: quem chegou pela raiz (`/`)
     gravaria um recente que só sabe redirecionar. */
  return RAIL.some((i) => i.chave === vista)
    ? { caminho: `/${CAMINHO_POR_VISTA[vista]}`, rotulo: TITULOS[vista], chave: vista }
    : null;
}
